import { Box, CircularProgress, Grid, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useNotification } from '../../../../hooks/NotificationContext';
import { getTestCycleKeyFromLocation } from '../../../../lib/executionUtils';
import { generateNotificationUrl } from '../../../../lib/notificationUtils';
import { getIssueMetadata, getIssuePriorities, getIssueTypes, getJiraLabels } from '../../../../modules/jira';
import FilePreview from '../../FilePreview';
import Modal from '../../../../ui/layout/Modal';
import FileUploadJira from '../attachments/FileUploadJira';
import JiraIssueLinkage from './JiraIssueLinkage';
import JiraUserSelector from './JiraUserSelector';
import LinkedProjectSelector from '../../../../features/jira/LinkedProjectSelector';
import MultiSelectDropdown from '../../../../ui/primitives/MultiSelectDropdown';
import ActionButton from '../../../../ui/primitives/ActionButton';
import CustomInputField from '../../../../ui/primitives/CustomInputField';
import CustomSelect from '../../../../ui/primitives/CustomSelect';

const MAX_ATTACHMENTS = 10;

const CreateJiraIssueForm = ({
  show,
  handleClose,
  fetchLinkedIssues,
  entityId,
  entityType,
  currentProject,
  testCaseData,
  entityKey,
  linkedJiraProjects,
  prefillData = null,
  setPrefillData = () => {},
  selectedProjectFromSearch = null,
  editOnly = false,
  onSaveEdits = null,
  currentUserJiraAccountId = null,
}) => {
  const mounted = useRef(true);
  const { addNotification } = useNotification();
  const location = useLocation();
  const { projectId } = useParams();
  const testCycleKey = getTestCycleKeyFromLocation(location);

  const [formData, setFormData] = useState({
    summary: '',
    description: '',
    issueType: '',
    assignee: '',
    reporter: '',
    priority: '',
  });
  const [jiraProjectId, setJiraProjectId] = useState(null);
  const [selectedLinkedProject, setSelectedLinkedProject] = useState(null);
  const [issueTypes, setIssueTypes] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [showPriorityField, setShowPriorityField] = useState(false);
  const [labels, setLabels] = useState([]);
  const [selectedLabels, setSelectedLabels] = useState([]);
  const [isLoadingLabels, setIsLoadingLabels] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  const [issueKey, setIssueKey] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    mounted.current = true;
    return () => (mounted.current = false);
  }, []);

  useEffect(() => {
    if (prefillData) {
      setFormData(prev => ({
        ...prev,
        summary: prefillData.summary || prev.summary,
        description: prefillData.description || prev.description,
        ...(prefillData.issueType ? { issueType: prefillData.issueType } : {}),
        ...(prefillData.priority  ? { priority:  prefillData.priority  } : {}),
        ...(prefillData.assignee  ? { assignee:  prefillData.assignee  } : {}),
        reporter: prefillData.reporter || currentUserJiraAccountId || prev.reporter,
      }));
      setAttachments(prefillData?.attachments?.length > 0 ? prefillData.attachments : []);
      if (prefillData.labels?.length > 0) setSelectedLabels(prefillData.labels);

      // ── Pre-select the project the user already chose on the review screen ──
      // Only applies in editOnly mode; normal mode uses the show-triggered effect below.
      if (editOnly && prefillData.selectedProject) {
        setSelectedLinkedProject(prefillData.selectedProject);
        fetchCreateIssueData(prefillData.selectedProject.linked_project_id);
      }
    }
  // fetchCreateIssueData is intentionally omitted to avoid an infinite loop;
  // it is a stable useCallback but updating state inside triggers re-renders.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillData, currentUserJiraAccountId, editOnly]);

  const checkPriorityFieldVisibility = useCallback(
    async (linkedProjectId, issueTypeId) => {
      try {
        const result = await getIssueMetadata(linkedProjectId, issueTypeId, projectId || currentProject.id);
        if (mounted.current) setShowPriorityField(Boolean(result.priority));
      } catch {
        if (mounted.current) setShowPriorityField(false);
      }
    },
    [projectId, currentProject?.id]
  );

  const fetchJiraLabels = useCallback(
    async (linkedProjectId) => {
      if (!linkedProjectId) return;
      setIsLoadingLabels(true);
      try {
        const result = await getJiraLabels(projectId || currentProject.id);
        if (mounted.current) {
          const formattedLabels = (result.labels || []).map(label => ({
            id: label,
            name: label,
          }));
          setLabels(formattedLabels);
        }
      } catch (error) {
        console.error('Failed to fetch Jira labels:', error);
        if (mounted.current) setLabels([]);
      } finally {
        if (mounted.current) setIsLoadingLabels(false);
      }
    },
    [projectId, currentProject?.id]
  );

  const fetchCreateIssueData = useCallback(
    async (linkedProjectId) => {
      if (!linkedProjectId) return;
      setLoading(true);
      setJiraProjectId(linkedProjectId);

      try {
        const [types, priorityList] = await Promise.all([
          getIssueTypes(linkedProjectId, projectId),
          getIssuePriorities(linkedProjectId, projectId),
        ]);
        fetchJiraLabels(linkedProjectId);
        if (!mounted.current) return;

        setIssueTypes(types || []);
        setPriorities(priorityList || []);

        setFormData(prev => {
          // In editOnly mode the issueType/priority come from prefillData — match by name.
          // In normal mode fall back to Bug / Medium defaults.
          const matchTypeByName  = (name) => types?.find(t => t.name === name)?.id;
          const matchPriorityByName = (name) => priorityList?.find(p => p.name === name)?.id;

          const defaultType     = types?.find(t => t.name === 'Bug') || types?.[0];
          const defaultPriority = priorityList?.find(p => p.name === 'Medium') || priorityList?.[0];

          // If prefillData supplied a string name (e.g. 'Story', 'High'), resolve to id.
          const resolvedIssueType = prev.issueType
            ? (matchTypeByName(prev.issueType) ?? prev.issueType)   // already an id? keep it
            : defaultType?.id || '';
          const resolvedPriority  = prev.priority
            ? (matchPriorityByName(prev.priority) ?? prev.priority)
            : defaultPriority?.id || '';

          return {
            ...prev,
            issueType: resolvedIssueType,
            priority:  resolvedPriority,
          };
        });

        setFormData(prev => {
          if (prev.issueType) {
            checkPriorityFieldVisibility(linkedProjectId, prev.issueType);
          }
          return prev;
        });
      } catch {
        if (mounted.current) setIssueTypes([]);
      } finally {
        if (mounted.current) setLoading(false);
      }
    },
    [projectId, checkPriorityFieldVisibility, fetchJiraLabels]
  );

  // Auto-select first project on open — skipped in editOnly (project comes from prefillData).
  useEffect(() => {
    if (show && linkedJiraProjects?.length > 0 && !selectedLinkedProject && !editOnly) {
      const first = selectedProjectFromSearch || linkedJiraProjects[0];
      setSelectedLinkedProject(first);
      fetchCreateIssueData(first.linked_project_id);
    }
  }, [show, linkedJiraProjects, selectedLinkedProject, selectedProjectFromSearch, fetchCreateIssueData, editOnly]);

  const handleProjectChange = useCallback(
    (project) => {
      setSelectedLinkedProject(project);
      setShowPriorityField(false);
      setPriorities([]);
      setLabels([]);
      setSelectedLabels([]);
      setFormData(prev => ({ ...prev, issueType: '', priority: '' }));
      fetchCreateIssueData(project?.linked_project_id);
    },
    [fetchCreateIssueData]
  );

  const handleInputChange = useCallback((id, value) => {
    setFormData(prev => ({ ...prev, [id]: value }));
  }, []);

  const handleDropdownChange = useCallback(
    (id, value) => {
      setFormData(prev => ({ ...prev, [id]: value || null }));
      if (id === 'issueType' && value && jiraProjectId) {
        checkPriorityFieldVisibility(jiraProjectId, value);
      }
    },
    [jiraProjectId, checkPriorityFieldVisibility]
  );

  const handleLabelsChange = useCallback((selectedIds) => {
    setSelectedLabels(selectedIds);
  }, []);

  const handleFiles = (files) => {
    if (attachments?.length + files.length > MAX_ATTACHMENTS) {
      setError('');
      setError(`You can only upload a total of ${MAX_ATTACHMENTS} files.`);
      return;
    }
    setAttachments(prev => [...prev, ...files]);
    setError('');
  };

  const handleRemoveImage = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
    setError('');
  };

  const handleSaveEditOnly = useCallback(() => {
    if (!formData.summary.trim()) {
      addNotification('danger', 'Please provide a summary');
      return;
    }
    if (!formData.reporter) {
      addNotification('danger', 'Please select a Reporter');
      return;
    }

    if (typeof onSaveEdits === 'function') {
      // Resolve IDs back to human-readable names so the review card displays
      // "Story" / "High" rather than raw Jira IDs like "10048" / "3".
      const issueTypeName = issueTypes.find((t) => t.id === formData.issueType)?.name ?? formData.issueType;
      const priorityName  = priorities.find((p) => p.id === formData.priority)?.name  ?? formData.priority;

      onSaveEdits(prefillData?.storyId, {
        summary:       formData.summary.trim(),
        description:   formData.description,
        issueType:     formData.issueType,
        issueTypeName,
        priority:      formData.priority,
        priorityName,
        assignee:      formData.assignee,
        reporter:      formData.reporter,
        labels:        selectedLabels,
        attachments,
        // ── Return the project the user may have switched to inside the form ──
        jiraProjectId: selectedLinkedProject?.linked_project_id ?? null,
      });
    }

    handleModalClose();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, selectedLabels, attachments, prefillData?.storyId, onSaveEdits, addNotification, selectedLinkedProject]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();

    if (editOnly) {
      handleSaveEditOnly();
      return;
    }

    if (!formData.summary.trim()) return addNotification('danger', 'Please provide a summary');
    if (!formData.issueType) return addNotification('danger', 'Please select an Issue Type');
    if (!formData.reporter) return addNotification('danger', 'Please select a Reporter');
    if (showPriorityField && !formData.priority) return addNotification('danger', 'Please select a Priority');

    const otherData = entityType === 'Execution' ? { testCaseKey: testCaseData?.key, testCycleKey } : null;
    const entityUrl = generateNotificationUrl(entityType, projectId, entityKey, 'traceability', otherData);

    const issuePayload = {
      fields: {
        summary: formData.summary.trim(),
        issuetype: { id: formData.issueType },
        project: { id: jiraProjectId },
        reporter: { id: formData.reporter },
        ...(showPriorityField ? { priority: { id: formData.priority } } : {}),
        ...(formData.assignee ? { assignee: { id: formData.assignee } } : {}),
        ...(formData.description ? { description: formData.description } : {}),
        ...(selectedLabels.length > 0 ? { labels: selectedLabels } : {}),
      },
      entityUrl,
      entityKey,
      entityType,
      entityId,
      attachments: prefillData?.attachments?.length > 0 ? prefillData?.attachments?.map(a => a.url) : [],
    };

    const unifiedFormData = new FormData();
    unifiedFormData.append('issueData', JSON.stringify(issuePayload));
    const filesToUpload = attachments?.filter(file => file instanceof File || file instanceof Blob);

    if (filesToUpload.length > 0) {
      filesToUpload.forEach((file) => {
        unifiedFormData.append('files', file);
      });
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const apiRoot = process.env.REACT_APP_API_URL || '';

      const response = await fetch(`${apiRoot}/api/v1/jira/create-issue`, {
        method: 'POST',
        body: unifiedFormData,
        headers: {
          'Authorization': `${token}`,
          'X-Project-Id': projectId,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        const errorMessage = errorData?.error?.message || 'Failed to create the Jira issue.';
        throw new Error(errorMessage);
      }

      const responseData = await response.json();
      const key = responseData?.jira?.key;

      fetchLinkedIssues();
      addNotification('success', 'Issue added successfully.');

      if (entityType === 'Execution') {
        setIssueKey(key);
        setStep(2);
      } else {
        handleModalClose();
      }
    } catch (error) {
      addNotification('danger', error.message || 'Failed to create the Jira issue.');
    } finally {
      if (mounted.current) setSubmitting(false);
    }
  };

  const handleModalClose = () => {
    if (submitting) return;
    setStep(1);
    setIssueKey('');
    setFormData({ summary: '', description: '', issueType: '', assignee: '', reporter: '', priority: '' });
    setAttachments([]);
    setSelectedLinkedProject(null);
    setSelectedLabels([]);
    setPrefillData(null);
    handleClose();
  };

  const isSubmitDisabled = editOnly
    ? submitting || !formData.summary || !formData.reporter
    : submitting || !formData.summary || !formData.issueType || !formData.reporter || (showPriorityField && !formData.priority);

  const primaryButtonLabel = editOnly
    ? 'Save'
    : submitting
    ? 'Creating...'
    : 'Create';

  const modalTitle = editOnly ? 'Edit Story' : step === 1 ? 'Create & Link Jira Issue' : 'Create Issue Links';

  return (
    <Modal
      open={show}
      handleClose={handleModalClose}
      width="sm"
      modalTitle={modalTitle}
      action={() => (
        <Stack direction="row" spacing={1}>
          <ActionButton
            label="Close"
            intent="secondary"
            onClick={handleModalClose}
            disabled={submitting}
          />
          {step === 1 && (
            <ActionButton
              label={primaryButtonLabel}
              intent="primary"
              onClick={handleSubmit}
              disabled={isSubmitDisabled}
              loading={submitting}
            />
          )}
        </Stack>
      )}
    >
      {loading || submitting ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: 200, gap: 2 }}>
          <CircularProgress />
          {submitting && !editOnly && <Typography variant="body2">Creating...</Typography>}
        </Box>
      ) : step === 1 ? (
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            {/* Project & Issue Type */}
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                <Typography variant="body2" fontWeight={600}>
                  Project <Box component="span" sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                </Typography>
                <LinkedProjectSelector
                  linkedProjects={linkedJiraProjects}
                  selectedProjectId={selectedLinkedProject?.linked_project_id}
                  onProjectChange={handleProjectChange}
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <CustomSelect
                id="issueType"
                label="Issue Type"
                required={!editOnly}
                value={formData.issueType}
                onChange={(value) => handleDropdownChange('issueType', value)}
                options={issueTypes.map(type => ({ id: type.id, name: type.name }))}
                placeholder="Select issue type"
                disabled={editOnly}
              />
            </Grid>

            {/* Summary */}
            <Grid item xs={12}>
              <CustomInputField
                id="summary"
                label="Summary"
                required
                value={formData.summary}
                onChange={handleInputChange}
                placeholder="Enter issue summary"
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <CustomInputField
                id="description"
                label="Description"
                value={formData.description}
                onChange={handleInputChange}
                multiline
                rows={3}
                placeholder="Enter issue description"
              />
            </Grid>

            {/* Reporter & Assignee */}
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                <Typography variant="body2" fontWeight={600}>
                  Reporter <Box component="span" sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                </Typography>
                <JiraUserSelector
                  value={formData.reporter}
                  onChange={(v) => handleDropdownChange('reporter', v)}
                  projectId={projectId}
                  jiraProjectId={jiraProjectId}
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                <Typography variant="body2" fontWeight={600}>
                  Assignee
                </Typography>
                <JiraUserSelector
                  value={formData.assignee}
                  onChange={(v) => handleDropdownChange('assignee', v)}
                  projectId={projectId}
                  jiraProjectId={jiraProjectId}
                />
              </Box>
            </Grid>

            {/* Labels & Priority */}
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                <Typography variant="body2" fontWeight={600}>
                  Labels
                </Typography>
                {isLoadingLabels ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">Loading labels...</Typography>
                  </Box>
                ) : (
                  <MultiSelectDropdown
                    options={labels}
                    selectedValues={selectedLabels}
                    placeholder="Select labels"
                    onChange={handleLabelsChange}
                    showSelectAll={false}
                    name="labels"
                    sx={{ mb: 0 }}
                  />
                )}
              </Box>
            </Grid>

            {showPriorityField && (
              <Grid item xs={12} sm={6}>
                <CustomSelect
                  id="priority"
                  label="Priority"
                  required={!editOnly}
                  value={formData.priority}
                  onChange={(value) => handleDropdownChange('priority', value)}
                  options={priorities.map(p => ({ id: p.id, name: p.name }))}
                  placeholder="Select priority"
                />
              </Grid>
            )}

            {/* Attachments */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                <Typography variant="body2" fontWeight={600}>
                  Attachments ({attachments?.length}/{MAX_ATTACHMENTS})
                </Typography>
                <FileUploadJira enableDragAndDrop handleFiles={handleFiles} />
                <FilePreview attachments={attachments} handleRemoveImage={handleRemoveImage} submitting={submitting} />
                {error && (
                  <Typography variant="caption" color="error.main" sx={{ mt: 0.5, display: 'block' }}>
                    {error}
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
      ) : (
        // step === 2 — only reachable in normal mode (Execution entity type)
        <JiraIssueLinkage
          testCaseId={testCaseData?.id}
          issueKey={issueKey}
          projectId={projectId}
          handleModalClose={handleModalClose}
          type="create"
          setSubmitting={setSubmitting}
        />
      )}
    </Modal>
  );
};

export default CreateJiraIssueForm;